import client from "@/api/openapi-ts";
import { ref } from "vue";
import { usePlugins } from "./helpers/usePlugins";
import { ProgramTimes } from "@/types/api/TvViewership";
import { defineStore, acceptHMRUpdate } from "pinia";

type PendingTaskId = {
  task_id: string;
};

type Geographies = {
  geographies: {
    exclude: [];
    include: [];
    join_type?: string;
  };
};

type NetworksRequest = {
  geographies: Geographies;
  tv_categories: [string];
};

type CallsignsRequest = {
  geographies: Geographies;
  networks: [string];
};

type ProgramsRequest = {
  geographies: Geographies;
  month: string;
  network: string;
  callsign: string;
};

type TimesRequest = {
  program: string;
  network: string;
  callsign: string;
};

export const useTvViewershipStore = defineStore("tvViewership", () => {
  const loadingNetworks = ref(false);
  const networksTaskId = ref<string>();
  const loadingCallsigns = ref(false);
  const callsignsTaskId = ref<string>();
  const networks = ref<string>();
  const callsigns = ref<string>();
  const loadingPrograms = ref(false);
  const programsTaskId = ref<string>();
  const programs = ref<string[]>();
  const loadingTimes = ref(false);
  const timesTaskId = ref<string>();
  const programTimes = ref<ProgramTimes[]>();
  const loadingDates = ref(false);
  const datesTaskId = ref<string>();
  const dates = ref();

  const { $ws } = usePlugins();

  return {
    loadingNetworks,
    networksTaskId,
    loadingCallsigns,
    callsignsTaskId,
    networks,
    callsigns,
    loadingPrograms,
    programsTaskId,
    programs,
    loadingTimes,
    timesTaskId,
    programTimes,
    loadingDates,
    datesTaskId,
    dates,
    async getNetworks(payload: NetworksRequest) {
      loadingNetworks.value = true;
      try {
        const { data } = await client.POST(
          "/api/v1/audience_tv_viewership/networks",
          { body: payload as never },
        );
        const taskId = (data as PendingTaskId | undefined)?.task_id;
        networksTaskId.value = taskId;
        if ($ws) {
          $ws.subscribe({
            channel: "TvViewership",
            payload: {
              id: taskId,
            },
          });
        }
      } catch (error) {
        console.error(error);
        loadingNetworks.value = false;
      }
    },
    async getCallsigns(payload: CallsignsRequest) {
      loadingCallsigns.value = true;
      try {
        const { data } = await client.POST(
          "/api/v1/audience_tv_viewership/callsigns",
          { body: payload as never },
        );
        const taskId = (data as PendingTaskId | undefined)?.task_id;
        callsignsTaskId.value = taskId;
        if ($ws) {
          $ws.subscribe({
            channel: "TvViewership",
            payload: {
              id: taskId,
            },
          });
        }
      } catch (error) {
        console.error(error);
        loadingCallsigns.value = false;
      }
    },
    async getTopPrograms(payload: ProgramsRequest) {
      loadingPrograms.value = true;
      try {
        const { data } = await client.POST(
          "/api/v1/audience_tv_viewership/top_programs",
          { body: payload as never },
        );
        const taskId = (data as PendingTaskId | undefined)?.task_id;
        programsTaskId.value = taskId;
        if ($ws) {
          $ws.subscribe({
            channel: "TvViewership",
            payload: {
              id: taskId,
            },
          });
        }
      } catch (error) {
        console.error(error);
        loadingPrograms.value = false;
      }
    },
    async getProgramTimes(payload: TimesRequest) {
      loadingTimes.value = true;
      try {
        const { data } = await client.POST(
          "/api/v1/audience_tv_viewership/program_times",
          { body: payload as never },
        );
        const taskId = (data as PendingTaskId | undefined)?.task_id;
        timesTaskId.value = taskId;
        if ($ws) {
          $ws.subscribe({
            channel: "TvViewership",
            payload: {
              id: taskId,
            },
          });
        }
      } catch (error) {
        console.error(error);
        loadingTimes.value = false;
      }
    },
    async getMonths() {
      loadingDates.value = true;
      try {
        const { data } = await client.POST(
          "/api/v1/audience_tv_viewership/months",
          { body: {} as never },
        );
        const taskId = (data as PendingTaskId | undefined)?.task_id;
        datesTaskId.value = taskId;
        if ($ws) {
          $ws.subscribe({
            channel: "TvViewership",
            payload: {
              id: taskId,
            },
          });
        }
      } catch (error) {
        console.error(error);
        loadingDates.value = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useTvViewershipStore, import.meta.hot),
  );
}
